import type { ApiKey } from "@webyonetim/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { PageHeader } from "../../components/shell/AppShell";
import { Button } from "../../components/ui/Controls";
import { EmptyState, ErrorState, Skeleton, StatusBadge } from "../../components/ui/Feedback";
import { ResponsiveData, type DataColumn } from "../../components/ui/ResponsiveData";
import {
  DestructiveConfirmDialog,
  ReauthenticationDialog,
  type ReauthenticationCredentials,
} from "../../components/ui/SecurityDialogs";
import { apiErrorMessage, useAuth } from "../../lib/auth";
import { formatDateTime } from "../../lib/format";
import { apiKeysApi, securityApi } from "../../lib/api";
import { ApiKeyCreateDialog, scopeLabels } from "./ApiKeyCreateDialog";

export function ApiKeysPage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["api-keys"], queryFn: apiKeysApi.list, retry: false });
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey>();
  const [reauthenticationId, setReauthenticationId] = useState<string>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const canManage = auth.identity?.permissions.includes("api_keys:manage") === true;
  useEffect(() => {
    if (revokeTarget === undefined) {
      setReauthenticationId(undefined);
      setError(undefined);
    }
  }, [revokeTarget]);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["api-keys"] });
  };
  const verify = async (credentials: ReauthenticationCredentials) => {
    setBusy(true);
    setError(undefined);
    try {
      const result = await securityApi.reauthenticate({
        password: credentials.password,
        ...(credentials.secondFactor === undefined
          ? {}
          : {
              secondFactor: /^\d{6}$/u.test(credentials.secondFactor)
                ? { method: "totp" as const, code: credentials.secondFactor }
                : { method: "recovery_code" as const, code: credentials.secondFactor },
            }),
      });
      setReauthenticationId(result.reauthenticationId);
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  const revoke = async () => {
    if (revokeTarget === undefined || reauthenticationId === undefined) return;
    setBusy(true);
    setError(undefined);
    try {
      await apiKeysApi.revoke(revokeTarget.id, {
        confirmationLabel: revokeTarget.label,
        reauthenticationId,
      });
      refresh();
      setRevokeTarget(undefined);
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  const columns: readonly DataColumn<ApiKey>[] = [
    {
      header: "Etiket",
      key: "label",
      render: (key) => (
        <>
          <strong>{key.label}</strong>
          <br />
          <code>{key.id}</code>
        </>
      ),
    },
    { header: "İzin özeti", key: "scopes", render: (key) => `${String(key.scopes.length)} kapsam` },
    {
      header: "Kaynak sınırı",
      key: "boundary",
      render: (key) =>
        key.resourceBoundary.mode === "all_resources"
          ? "Tüm kaynaklar"
          : `${String(key.resourceBoundary.projectIds.length)} proje · ${String(key.resourceBoundary.hostIds.length)} sunucu`,
    },
    {
      header: "Oluşturulma",
      key: "created",
      render: (key) => <time dateTime={key.createdAt}>{formatDateTime(key.createdAt)}</time>,
    },
    {
      header: "Son kullanım",
      key: "last-used",
      render: (key) =>
        key.lastUsedAt === undefined ? (
          "Henüz kullanılmadı"
        ) : (
          <time dateTime={key.lastUsedAt}>{formatDateTime(key.lastUsedAt)}</time>
        ),
    },
    {
      header: "Son kullanma",
      key: "expires",
      render: (key) => <time dateTime={key.expiresAt}>{formatDateTime(key.expiresAt)}</time>,
    },
    { header: "Durum", key: "status", render: (key) => <StatusBadge status={key.status} /> },
    {
      header: "İşlemler",
      key: "actions",
      render: (key) =>
        canManage && key.status !== "revoked" ? (
          <Button
            onClick={() => {
              setRevokeTarget(key);
            }}
            variant="destructive"
          >
            Anahtarı iptal et
          </Button>
        ) : (
          <span className="muted">İşlem yok</span>
        ),
    },
  ];
  return (
    <div className="page">
      <PageHeader
        action={
          canManage ? (
            <Button
              onClick={() => {
                setCreateOpen(true);
              }}
            >
              API anahtarı oluştur
            </Button>
          ) : undefined
        }
        description="Otomasyonlara yalnızca gereken kaynak ve işlemler için süreli erişim verin."
        title="API Anahtarları"
      />
      {query.isLoading ? (
        <Skeleton />
      ) : query.isError ? (
        <ErrorState
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : query.data?.items.length === 0 ? (
        <EmptyState
          action={
            canManage ? (
              <Button
                onClick={() => {
                  setCreateOpen(true);
                }}
              >
                API anahtarı oluştur
              </Button>
            ) : undefined
          }
          body="İlk anahtarınızı yalnızca gereken izinlerle ve sınırlı bir süre için oluşturun."
          heading="Henüz API anahtarı yok"
        />
      ) : query.data === undefined ? null : (
        <section className="card">
          <ResponsiveData
            caption="API anahtarları"
            columns={columns}
            getKey={(key) => key.id}
            items={query.data.items}
            mobileHeading={(key) => key.label}
          />
        </section>
      )}
      <p>
        <a href="/api/v1/openapi.json" rel="noopener" target="_blank">
          OpenAPI belgesini aç
        </a>
      </p>
      <ApiKeyCreateDialog onCreated={refresh} onOpenChange={setCreateOpen} open={createOpen} />
      {revokeTarget === undefined ? null : reauthenticationId === undefined ? (
        <ReauthenticationDialog
          actionName={`“${revokeTarget.label}” API anahtarını iptal et`}
          error={error}
          onOpenChange={(next) => {
            if (!next) setRevokeTarget(undefined);
          }}
          onVerify={verify}
          open
          requireSecondFactor={auth.identity?.user.totpEnabled === true}
          submitting={busy}
        />
      ) : (
        <DestructiveConfirmDialog
          confirmationLabel={revokeTarget.label}
          consequence={`“${revokeTarget.label}” API anahtarı iptal edilecek. Bu anahtarı kullanan otomasyonlar hemen durabilir.`}
          error={error}
          finalAction="Anahtarı iptal et"
          name="API anahtarını iptal et"
          onConfirm={revoke}
          onOpenChange={(next) => {
            if (!next) setRevokeTarget(undefined);
          }}
          open
          submitting={busy}
        />
      )}
      <details className="card">
        <summary>Kapsam açıklamaları</summary>
        <ul>
          {Object.entries(scopeLabels).map(([code, label]) => (
            <li key={code}>
              <code>{code}</code> — {label}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
